import type { GraphVenue } from "@/api/discover";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { formatDay, formatLocation } from "./discoverCopy";

type Props = {
  venue: GraphVenue | null;
  onClose: () => void;
};

export default function VenueEvidenceSheet({ venue, onClose }: Props) {
  return (
    <Sheet open={Boolean(venue)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        {venue && (
          <>
            <SheetHeader>
              <SheetTitle>{venue.name}</SheetTitle>
              <SheetDescription>
                {formatLocation([venue.city, venue.region, venue.country]) || "Location unknown"}
                {venue.capacity ? ` · ${venue.capacity.toLocaleString()} capacity` : ""}
              </SheetDescription>
            </SheetHeader>

            <div className="mt-4 space-y-4">
              <p className="text-sm text-foreground">
                Played by {venue.comparable_artist_count} of your comparable artist
                {venue.comparable_artist_count === 1 ? "" : "s"}.
              </p>

              <ul className="space-y-2" aria-label="Observed performances">
                {venue.events.map((event, index) => (
                  <li
                    key={`${event.public_artist_id}-${event.event_date}-${index}`}
                    className="flex items-start justify-between gap-3 rounded-lg border border-border bg-background/40 p-3 text-sm"
                  >
                    <div>
                      <p className="font-medium text-foreground">{event.artist_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatDay(event.event_date)}
                        {event.is_past ? "" : " · upcoming"}
                      </p>
                    </div>
                    {event.ticket_url && (
                      <a
                        href={event.ticket_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-xs font-medium text-primary hover:underline"
                      >
                        Event listing ↗
                      </a>
                    )}
                  </li>
                ))}
              </ul>
              {venue.event_count > venue.events.length && (
                <p className="text-xs text-muted-foreground">
                  Showing the {venue.events.length} most recent of {venue.event_count} performances.
                </p>
              )}

              <p className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
                These are performances we've observed from public listings. They don't confirm current availability,
                contacts or deal terms.
              </p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
